import React, { useContext, useMemo, useState, useEffect, useCallback, useRef } from 'react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import MaskedInput from 'react-text-mask';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';

import UserContext from '@/contexts/UserContext';
import AddressService from '@/services/addressService';
import FieldErrorDisplay from './FieldErrorDisplay';

const customSelectStyles = (hasError, isDisabled) => ({
  control: (base, state) => ({
    ...base,
    minHeight: '37px',
    height: '37px',
    borderRadius: '4px',
    border: `1px solid ${hasError ? '#dc3545' : isDisabled ? '#ced4da' : state.isFocused ? '#80bdff' : '#ced4da'}`,
    boxShadow: state.isFocused && !hasError ? '0 0 0 0.2rem rgba(0,123,255,.25)' : 'none',
    fontSize: '14px',
    fontFamily: 'Open Sans',
    fontWeight: 'normal',
    backgroundColor: isDisabled ? '#efefef' : 'white',
    cursor: isDisabled ? 'not-allowed' : 'default',
    '&:hover': {
      borderColor: hasError ? '#dc3545' : isDisabled ? '#ced4da' : state.isFocused ? '#80bdff' : '#ced4da',
    },
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: '14px',
    fontWeight: 'normal',
    color: isDisabled ? 'rgba(0,0,0,0.38)' : '#212529',
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 12px',
    height: '100%',
  }),
  
  indicatorSeparator: () => ({ display: 'none' }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    color: isDisabled ? 'rgba(0,0,0,0.38)' : '#919191',
    '& input': {
      height: '100% !important',
    },
  }),
  placeholder: (base) => ({
    ...base,
    fontSize: '14px',
    fontWeight: 'normal',
    color: '#b1b1b1',
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '14px',
    fontWeight: 'normal',
    fontFamily: 'Open Sans',
    backgroundColor: state.isSelected ? '#5882C1' : state.isFocused ? '#f0f4f9' : 'transparent',
    color: state.isSelected ? 'white' : '#374151',
    '&:active': { backgroundColor: '#5882C1' },
  }),
});

const ADDRESS_FIELDS = ['zipcode', 'state', 'city', 'neighborhood', 'street', 'number', 'complement'];

const normalizeAddressData = (address = {}) =>
  ADDRESS_FIELDS.reduce(
    (acc, key) => ({
      ...acc,
      [key]: typeof address?.[key] === 'string' ? address[key] : address?.[key] || '',
    }),
    {}
  );

const isSameAddress = (first = {}, second = {}) =>
  ADDRESS_FIELDS.every((key) => (first?.[key] || '') === (second?.[key] || ''));

const AddressSelector = ({ addressData = {}, onChange, disabled, errors, required, schema }) => {
  const me = useContext(UserContext);
  const intl = useIntl();
  const country = me?.profile?.country || 'BR';
  const isColombianFranchisee = country === 'CO';

  const hasFieldError = (fieldName) => errors?.[fieldName]?.__errors?.length > 0;

  const isFieldRequired = useCallback(
    (fieldName) => {
      if (schema?.required?.includes(fieldName)) return true;
      if (required && fieldName !== 'complement') return true;
      return false;
    },
    [schema, required]
  );

  const normalizedAddress = useMemo(() => normalizeAddressData(addressData), [addressData]);
  const [localZipcode, setLocalZipcode] = useState(normalizedAddress.zipcode || '');


  useEffect(() => {
    setLocalZipcode(normalizedAddress.zipcode || '');
  }, [normalizedAddress.zipcode]);

  const updateAddress = useCallback(
    (updates) => {
      onChange({ ...addressData, ...updates });
    },
    [addressData, onChange]
  );

  const addressService = useMemo(() => new AddressService(country), [country]);
  const useTextInputForAddress = ['UY', 'PY', 'MX', 'CO'].includes(country);

  const getLocationMutation = useMutation({
    mutationFn: async (zipcode) => await addressService.getLocationByZipcode(zipcode),
  });

  const { data: states = [], isLoading: isLoadingStates } = useQuery({
    queryKey: ['addressStates', country],
    queryFn: () => addressService.getStates(),
    staleTime: Infinity,
  });

  const selectedStateId = useMemo(() => {
    if (!normalizedAddress.state || !states.length) return null;
    const stateMatch = states.find((state) => state.name === normalizedAddress.state);
    return stateMatch ? stateMatch.id : null;
  }, [normalizedAddress.state, states]);

  const { data: cities = [], isLoading: isLoadingCities } = useQuery({
    queryKey: ['addressCities', country, selectedStateId],
    queryFn: () => addressService.getCitiesByState(selectedStateId),
    enabled: !!selectedStateId && !useTextInputForAddress,
  });

  const selectedCityId = useMemo(() => {
    if (!normalizedAddress.city || !cities.length) return null;
    const cityMatch = cities.find((city) => city.name === normalizedAddress.city);
    return cityMatch ? (cityMatch.id || cityMatch.name) : null;
  }, [normalizedAddress.city, cities]);

  const { data: neighborhoods = [], isLoading: isLoadingNeighborhoods } = useQuery({
    queryKey: ['addressNeighborhoods', country, selectedCityId],
    queryFn: () => addressService.getNeighborhoodsByCity(selectedCityId),
    enabled: !!selectedCityId && !useTextInputForAddress,
  });

  const stateOptions = useMemo(
    () => states.map((state) => ({ label: state.name, value: state.id, name: state.name })),
    [states]
  );
  const cityOptions = useMemo(
    () => cities.map((city) => ({ label: city.name, value: city.id || city.name, name: city.name })),
    [cities]
  );
  const neighborhoodOptions = useMemo(
    () =>
      neighborhoods.map((neighborhood) => ({
        label: neighborhood.name,
        value: neighborhood.id || neighborhood.name,
        name: neighborhood.name,
      })),
    [neighborhoods]
  );

  const handleZipcodeChange = useCallback((event) => {
    const zipcode = event.target.value;
    setLocalZipcode(zipcode);

    const numericZipcode = zipcode.replace(/\D/g, '');
    const requiredLength = { BR: 8, PT: 7, UY: 5, PY: 6, MX: 5, CO: 6 }[country] || 6;

    if (numericZipcode.length >= requiredLength) {
      getLocationMutation.mutate(zipcode, {
        onSuccess: (fetchedAddress) => {
          if (fetchedAddress && !fetchedAddress.error) {
            updateAddress({
              zipcode: fetchedAddress.zipcode || zipcode,
              state: fetchedAddress.state || normalizedAddress.state,
              city: fetchedAddress.city || normalizedAddress.city,
              neighborhood: fetchedAddress.neighborhood || normalizedAddress.neighborhood,
              street: fetchedAddress.street || normalizedAddress.street,
            });
          } else {
            updateAddress({ zipcode });
          }
        },
        onError: () => {
          updateAddress({ zipcode });
        }
      });
    } else {
      updateAddress({ zipcode });
    }
  }, [country, getLocationMutation, normalizedAddress.state, normalizedAddress.city, normalizedAddress.neighborhood, normalizedAddress.street, updateAddress]);

  return (
    <div className="flex flex-col gap-y-4">
      <div className={`grid grid-cols-1 gap-x-[30px] gap-y-4 ${isColombianFranchisee ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
        {!isColombianFranchisee && (
          <div className="flex flex-col">
            <label className="mb-1 mt-[0.3vh] inline-block font-['Open_Sans'] text-[12px] text-[#212529]">
              {intl.formatMessage({ id: 'zipcode' })} {isFieldRequired('zipcode') && '*'}
            </label>
            <div className="relative">
              <MaskedInput
                disabled={disabled}
                guide={false}
                value={localZipcode}
                onChange={handleZipcodeChange}
                placeholder={intl.formatMessage({ id: 'address.zipcode.placeholder' })}
                mask={addressService.getZipcodeMask()}
                style={{ fontWeight: 'normal', color: disabled ? 'rgba(0, 0, 0, 0.38)' : '#212529' }}
                className={`w-full rounded-[4px] !h-[37px] !px-[12px] py-[6px] font-['Open_Sans'] text-[14px] font-normal outline-none transition-all placeholder:text-gray-400 border-[1px] border-solid focus:border-[#80bdff] ${
                  disabled ? 'bg-[#efefef] cursor-not-allowed' : 'bg-white'
                } ${hasFieldError('zipcode') ? 'border-[#dc3545] focus:border-[#dc3545]' : 'border-[#ced4da] focus:border-[#80bdff]'}`}
              />
              {getLocationMutation.isPending && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-[#4055b3] border-t-transparent" />
                </div>
              )}
            </div>
            <FieldErrorDisplay errorSchema={errors} fieldName="zipcode" />
          </div>
        )}

        <div className="flex flex-col">
          <label className="mb-1 mt-[0.3vh] inline-block font-['Open_Sans'] text-[12px] text-[#212529]">
            {intl.formatMessage({ id: 'state' })} {isFieldRequired('state') && '*'}
          </label>
          {useTextInputForAddress ? (
            <input
              type="text"
              disabled={disabled}
              value={normalizedAddress.state || ''}
              onChange={(event) => updateAddress({ state: event.target.value })}
              style={{ fontWeight: 'normal', color: disabled ? 'rgba(0, 0, 0, 0.38)' : '#212529' }}
              className={`w-full rounded-[4px] !h-[37px] !px-[12px] py-[6px] font-['Open_Sans'] text-[14px] font-normal outline-none transition-all placeholder:text-gray-400 border-[1px] border-solid ${
                disabled ? 'bg-[#efefef] cursor-not-allowed' : 'bg-white'
              } ${hasFieldError('state') ? 'border-[#dc3545] focus:border-[#dc3545]' : 'border-[#ced4da] focus:border-[#80bdff]'}`}
            />
          ) : (
            <Select
              options={stateOptions}
              value={normalizedAddress.state ? { label: normalizedAddress.state, value: selectedStateId || normalizedAddress.state } : null}
              onChange={(option) =>
                updateAddress({
                  state: option?.name || option?.label || '',
                  city: '',
                  neighborhood: '',
                })
              }
              isDisabled={disabled || isLoadingStates || getLocationMutation.isPending}
              placeholder={intl.formatMessage({ id: 'address.select.placeholder' })}
              styles={customSelectStyles(hasFieldError('state'), disabled)}
            />
          )}
          <FieldErrorDisplay errorSchema={errors} fieldName="state" />
        </div>

        <div className="flex flex-col">
          <label className="mb-1 mt-[0.3vh] inline-block font-['Open_Sans'] text-[12px] text-[#212529]">
            {intl.formatMessage({ id: 'city' })} {isFieldRequired('city') && '*'}
          </label>
          {useTextInputForAddress ? (
            <input
              type="text"
              disabled={disabled}
              value={normalizedAddress.city || ''}
              onChange={(event) => updateAddress({ city: event.target.value })}
              style={{ fontWeight: 'normal', color: disabled ? 'rgba(0, 0, 0, 0.38)' : '#212529' }}
              className={`w-full rounded-[4px] !h-[37px] !px-[12px] py-[6px] font-['Open_Sans'] text-[14px] font-normal outline-none transition-all placeholder:text-gray-400 border-[1px] border-solid ${
                disabled ? 'bg-[#efefef] cursor-not-allowed' : 'bg-white'
              } ${hasFieldError('city') ? 'border-[#dc3545] focus:border-[#dc3545]' : 'border-[#ced4da] focus:border-[#80bdff]'}`}
            />
          ) : (
            <CreatableSelect
              options={cityOptions}
              value={normalizedAddress.city ? { label: normalizedAddress.city, value: selectedCityId || normalizedAddress.city } : null}
              onChange={(option) =>
                updateAddress({
                  city: option?.name || option?.label || '',
                  neighborhood: '',
                })
              }
              isDisabled={disabled || !selectedStateId || isLoadingCities || getLocationMutation.isPending}
              placeholder={intl.formatMessage({ id: 'address.select.placeholder' })}
              formatCreateLabel={(inputValue) => `${intl.formatMessage({ id: 'INSERT' })}: "${inputValue}"`}
              styles={customSelectStyles(hasFieldError('city'), disabled)}
            />
          )}
          <FieldErrorDisplay errorSchema={errors} fieldName="city" />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-x-[30px] gap-y-4 md:grid-cols-2">
        <div className="flex flex-col">
          <label className="mb-1 mt-[0.3vh] inline-block font-['Open_Sans'] text-[12px] text-[#212529]">
            {intl.formatMessage({ id: 'neighborhood' })} {isFieldRequired('neighborhood') && '*'}
          </label>
          {useTextInputForAddress ? (
            <input
              type="text"
              disabled={disabled}
              value={normalizedAddress.neighborhood || ''}
              onChange={(event) => updateAddress({ neighborhood: event.target.value })}
              style={{ fontWeight: 'normal', color: disabled ? 'rgba(0, 0, 0, 0.38)' : '#212529' }}
              className={`w-full rounded-[4px] !h-[37px] !px-[12px] py-[6px] font-['Open_Sans'] text-[14px] font-normal outline-none transition-all placeholder:text-gray-400 border-[1px] border-solid ${
                disabled ? 'bg-[#efefef] cursor-not-allowed' : 'bg-white'
              } ${hasFieldError('neighborhood') ? 'border-[#dc3545] focus:border-[#dc3545]' : 'border-[#ced4da] focus:border-[#80bdff]'}`}
            />
          ) : (
            <CreatableSelect
              options={neighborhoodOptions}
              value={
                normalizedAddress.neighborhood
                  ? {
                      label: normalizedAddress.neighborhood,
                      value: normalizedAddress.neighborhood,
                    }
                  : null
              }
              onChange={(option) =>
                updateAddress({
                  neighborhood: option?.name || option?.label || '',
                })
              }
              isDisabled={disabled || !selectedCityId || isLoadingNeighborhoods || getLocationMutation.isPending}
              placeholder={intl.formatMessage({ id: 'address.select.placeholder' })}
              formatCreateLabel={(inputValue) => `${intl.formatMessage({ id: 'INSERT' })}: "${inputValue}"`}
              styles={customSelectStyles(hasFieldError('neighborhood'), disabled)}
            />
          )}
          <FieldErrorDisplay errorSchema={errors} fieldName="neighborhood" />
        </div>

        <div className="flex flex-col">
          <label className="mb-1 mt-[0.3vh] inline-block font-['Open_Sans'] text-[12px] text-[#212529]">
            {intl.formatMessage({ id: 'street' })} {isFieldRequired('street') && '*'}
          </label>
          <input
            type="text"
            disabled={disabled}
            value={normalizedAddress.street || ''}
            onChange={(event) => updateAddress({ street: event.target.value })}
            placeholder={intl.formatMessage({ id: 'address.street.placeholder' })}
            style={{ fontWeight: 'normal', color: disabled ? 'rgba(0, 0, 0, 0.38)' : '#212529' }}
            className={`w-full rounded-[4px] !h-[37px] !px-[12px] py-[6px] font-['Open_Sans'] text-[14px] font-normal outline-none transition-all placeholder:text-gray-400 border-[1px] border-solid ${
              disabled ? 'bg-[#efefef] cursor-not-allowed' : 'bg-white'
            } ${hasFieldError('street') ? 'border-[#dc3545] focus:border-[#dc3545]' : 'border-[#ced4da] focus:border-[#80bdff]'}`}
          />
          <FieldErrorDisplay errorSchema={errors} fieldName="street" />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-x-[30px] gap-y-4 md:grid-cols-2">
        <div className="flex flex-col">
          <label className="mb-1 mt-[0.3vh] inline-block font-['Open_Sans'] text-[12px] text-[#212529]">
            {intl.formatMessage({ id: 'number' })} {isFieldRequired('number') && '*'}
          </label>
          <input
            type="text"
            disabled={disabled}
            value={normalizedAddress.number || ''}
            onChange={(event) => updateAddress({ number: event.target.value })}
            placeholder={intl.formatMessage({ id: 'address.number.placeholder' })}
            style={{ fontWeight: 'normal', color: disabled ? 'rgba(0, 0, 0, 0.38)' : '#212529' }}
            className={`w-full rounded-[4px] !h-[37px] !px-[12px] py-[6px] font-['Open_Sans'] text-[14px] font-normal outline-none transition-all placeholder:text-gray-400 border-[1px] border-solid ${
              disabled ? 'bg-[#efefef] cursor-not-allowed' : 'bg-white'
            } ${hasFieldError('number') ? 'border-[#dc3545] focus:border-[#dc3545]' : 'border-[#ced4da] focus:border-[#80bdff]'}`}
          />
          <FieldErrorDisplay errorSchema={errors} fieldName="number" />
        </div>

        <div className="flex flex-col">
          <label className="mb-1 mt-[0.3vh] inline-block font-['Open_Sans'] text-[12px] text-[#212529]">
            {intl.formatMessage({ id: 'complement' })}
          </label>
          <input
            type="text"
            disabled={disabled}
            value={normalizedAddress.complement || ''}
            onChange={(event) => updateAddress({ complement: event.target.value })}
            placeholder={intl.formatMessage({ id: 'address.complement.placeholder' })}
            style={{ fontWeight: 'normal', color: disabled ? 'rgba(0, 0, 0, 0.38)' : '#212529' }}
            className={`w-full rounded-[4px] !h-[37px] !px-[12px] py-[6px] font-['Open_Sans'] text-[14px] font-normal outline-none transition-all placeholder:text-gray-400 border-[1px] border-solid ${
              disabled ? 'bg-[#efefef] cursor-not-allowed' : 'bg-white'
            } ${hasFieldError('complement') ? 'border-[#dc3545] focus:border-[#dc3545]' : 'border-[#ced4da] focus:border-[#80bdff]'}`}
          />
          <FieldErrorDisplay errorSchema={errors} fieldName="complement" />
        </div>
      </div>
    </div>
  );
};

AddressSelector.propTypes = {
  addressData: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  errors: PropTypes.object,
  required: PropTypes.bool,
  schema: PropTypes.object,
};

export default AddressSelector;
